export interface IDedicatedTeamsState {
  dedicatedDevelopmentTeamsSection: IDedicatedDevelopmentTeamsSection;
  newProjectSection: INewProjectSection;
  FAQSection: IFAQSection;
  outsourcingToDedicatedTeamsSection: IOutsourcingToDedicatedTeamsSection;
  whatIsDedicatedTeamSection: IWhatIsDedicatedTeamSection;
  whyHireDedicatedTeamSection: IWhyHireDedicatedTeamListSection;
  benefitsOfDedicatedTeamsSection: IDedicatedTeamListSection;
  comparingDedicatedTeamSection: IComparingDedicatedTeamSection;
  howToHireTeamSection: IHowToHireTeamSection;
  howToManageTeamSection: IHowToManageTeamSection;
  bestOptionSection: IBestOptionSectionSection;
  startBusiness: IDedicatedStartBusiness;
}
export type IDedicatedStartBusiness = {
  title: string;
  description: string;
};
export interface IDedicatedDevelopmentTeamsSection {
  imageSrc: string;
  title: string;
  text: string[];
}

export interface INewProjectSection {
  title: string;
  buttonText: string;
}

export interface IFAQSection {
  title: string;
  accordionItems: FAQAccordionItems[];
}

export interface FAQAccordionItems {
  question: string;
  answers: string[];
}

export interface IOutsourcingToDedicatedTeamsSection {
  imageSrc: string;
  title: string;
  text: string[];
  list: string[];
}

interface IWhatIsDedicatedTeamSection {
  imageSrc: string;
  title: string;
  paragraphs: IWhatIsDedicatedTeamParagraphs[];
}

interface IWhatIsDedicatedTeamParagraphs {
  title: string;
  text: string;
}

export interface IDedicatedTeamListSection {
  title: string;
  description: string;
  items: IDedicatedTeamListItem[];
}

export interface IWhyHireDedicatedTeamListSection {
  title: string;
  description: string;
  items: IWhyHireDedicatedTeamListItem[];
}

export interface IWhyHireDedicatedTeamListItem {
  order: number;
  title: string;
  text: string;
}
export interface IDedicatedTeamListItem {
  iconSrc: string;
  title: string;
  text: string;
}

export interface IComparingDedicatedTeamSection {
  title: string;
  models: IComparingDedicatedTeamModel[];
}

export interface IComparingDedicatedTeamModel {
  id: number;
  tabTitle: string;
  tabType: 'time&material' | 'fixedPrice';
  description: string;
  tables: IComparingDedicatedTeamTable[];
}

export interface IComparingDedicatedTeamTable {
  title: string;
  text: string[];
}

export interface IHowToHireTeamSection {
  title: string;
  steps: IHowToHireTeamStep[];
}

export interface IHowToHireTeamStep {
  id: number;
  title: string;
  description: string[];
}

export interface IHowToManageTeamSection {
  title: string;
  description: string[];
  imageSrc: string;
  advices: IHowToManageTeamAdvice[];
}

export interface IHowToManageTeamAdvice {
  title: string;
  description: string[];
}

export interface IBestOptionSectionSection {
  title: string;
  description: string;
  industriesTitle: string;
  industries: IBestOptionSectionIndustries[];
  card: IOutsourcingToDedicatedTeamsSection;
}

export interface IBestOptionSectionIndustries {
  iconSrc: string;
  title: string;
  text: string;
}

export const DedicatedTeamsState: IDedicatedTeamsState = {
  dedicatedDevelopmentTeamsSection: {
    imageSrc: `/services-images/dedicated-teams/dedicated-teams.png`,
    title: `<h1>Dedicated software development teams</h1>`,
    text: [
      'Hire dedicated software development teams to foster innovation and accountability. A DT becomes fully immersed in your product ecosystem, taking responsibility for driving tech improvements and innovations to strengthen the overall product value.',
    ],
  },
  whatIsDedicatedTeamSection: {
    imageSrc: '/services-images/dedicated-teams/what-dedicated-teams.png',
    title: 'Scale faster with a dedicated team',
    paragraphs: [
      {
        title: '',
        text: `Working with dedicated team services offers numerous advantages that foster enduring partnerships.`,
      },
      {
        title: '',
        text: "<ul><li>The teams are preassembled, leading to a faster time to market. This enables them to focus exclusively on your product.</li><li>Your internal leadership can transition to strategic initiatives by delegating the core development and technical execution to the dedicated team.</li><li>This model works as an in-house team extension, enabling you to scale up and down developers based on the project's needs.</li><li>Enjoy cost transparency, since the DT model ensures detailed time tracking and regular reporting, so you know for sure the rate and efforts.</li><li>Leveraging worldwide talents enables you to reduce recruitment expenses, including HR wages, advertisement costs, and training.</li></ul>",
      },
    ],
  },
  whyHireDedicatedTeamSection: {
    title: 'Why hire a dedicated development team?',
    description:
      'A dedicated development team can be strategically deployed across various business scenarios to address specific organizational needs.',
    items: [
      {
        order: 1,
        title: 'Early-stage startups agility',
        text: 'Not only does the model enable startups to expand or narrow the team, it’s about flexible development process. The project priorities can be adjusted mid-sprints without legal or logistical friction.',
      },
      {
        order: 2,
        title: 'Undefined project scope',
        text: 'A DT model can significantly influence the project discovery phase to decompose the elements of ambiguity. The cross-functional expertise helps cover business, technical, and user perspectives simultaneously.',
      },
      {
        order: 3,
        title: 'Long-term projects',
        text: 'Deep knowledge of the software’s codebase reduces the likelihood of new and repetitive bugs emerging, which will streamline the final project delivery and is suitable for a long-term strategy.',
      },
    ],
  },
  benefitsOfDedicatedTeamsSection: {
    title: 'Advantages of hiring a dedicated development team',
    description:
      'Top-tier companies like Apple, Amazon, and WhatsApp use DT in their broader product development, balancing in-house resources and external partnerships. The model drives clear benefits outlined below.',
    items: [
      {
        iconSrc: '/services-images/dedicated-teams/check-icon.png',
        title: 'Setting priorities',
        text: 'DT promotes adherence to the stable roadmap. If a roadblock appears, the team works on critical tasks to remain on schedule.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/team-icon.png',
        title: 'Flexible team',
        text: 'Scale up development during peak phases by adding frontend devs or QA experts. Scale down resources for post-launch maintenance.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/star-icon.png',
        title: 'Extensive talent network',
        text: 'Cut development costs by addressing skill gaps. A dedicated partner sources niche specialists across all seniority levels.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/control-icon.png',
        title: 'High degree of control',
        text: 'Enjoy near-full control by selecting specific project management tools and dev methodologies to have a set workflow.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/process-icon.png',
        title: 'Well-structured processes',
        text: 'DT is a long-term cooperation model promoting cost optimization. It has established workflow methods for planning.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/cost-icon.png',
        title: 'Cost-effectiveness',
        text: 'With transparent pricing, you have to pay a monthly fee per team member involved for the actual work done and hours used.',
      },
    ],
  },
  comparingDedicatedTeamSection: {
    title: 'Comparing the dedicated team model vs others',
    models: [
      {
        id: 1,
        tabTitle: 'Fixed-Price',
        tabType: 'fixedPrice',
        description:
          'The dedicated team model is often mistakenly confused with fixed-price, as it’s designed to meet different business needs.',
        tables: [
          {
            title: 'Fixed-price model',
            text: [
              'The client pays via the predetermined fixed rate for the project, irrespective of the actual hours spent on the work.',
              'This model fits perfectly with projects with short duration and well-defined requirements (e.g., MVP).',
              'You must pay the full contract rate even if the project is under-delivered or incomplete.',
              'The fixed-price model is used when the project requirements are completely finalized with no anticipated alterations.',
            ],
          },
          {
            title: 'Dedicated team model',
            text: [
              'Payment is calculated based on a set hourly rate for each team member’s time.',
              'The DT model prioritizes projects that require flexibility due to the evolving requirements or lack a clear scope.',
              'Billing is tied to the actual development hours used to complete the project.',
              'You have complete flexibility to introduce changes or alter the scope at any project development stage.',
            ],
          },
        ],
      },
      {
        id: 2,
        tabTitle: 'Time & Materials',
        tabType: 'time&material',
        description:
          'The time & materials model is another engagement strategy that is usually confused with a dedicated team engagement.',
        tables: [
          {
            title: 'Time & materials model',
            text: [
              'The final product cost cannot be estimated upfront, so you pay for the actual hours and efforts the team applied to build it.',
              'This collaboration model is flexible, meaning that if the workload decreases, the members are assigned to other projects.',
              'This model is best suited for long-duration projects.',
            ],
          },
          {
            title: 'Dedicated team model',
            text: [
              'The model offers a predictable and transparent budgeting as you pay a fixed monthly fee determined by the team’s size.',
              'With a DT, you benefit from consistent expertise by working with the same specialists for the entire project development cycle.',
              'It’s a perfect fit for the determined long-term projects.',
            ],
          },
        ],
      },
    ],
  },
  howToHireTeamSection: {
    title: 'How we help you hire a dedicated team without risks',
    steps: [
      {
        id: 1,
        title: 'Define your needs',
        description: [
          'Agilie conducts in-depth discovery analysis to validate assumptions and map out the client’s strategic goals, technical scope tied to the financial budget. The business receives a clear, formulated strategy including well-defined goals and a structured budget.',
        ],
      },
      {
        id: 2,
        title: 'Validate the DT model',
        description: [
          'Based on the defined scope, we advise on the suitability of the DT models over the alternatives, like fixed-price or staff augmentation. Agilie crafts contract terms to enable future flexibility in resource scaling and adapting to evolving requirements.',
        ],
      },
      {
        id: 3,
        title: 'Team selection & interview',
        description: [
          'Agilie manages full-cycle development through our global talent network and HR expertise, handling sourcing, technical assessments, and background checks. Clients interview finalists and receive a managed team with fast onboarding.',
        ],
      },
      {
        id: 4,
        title: 'Kick-off & integration',
        description: [
          'We manage administrative, technical, and logistical setup, including workspace and equipment provisioning. The dedicated team follows clear Agile ceremonies integrated with your workflows, ensuring fast, seamless software development.',
        ],
      },
      {
        id: 5,
        title: 'Set KPIs & track results',
        description: [
          'We define Key Performance Indicators with clients, including development velocity, code quality, and feature completion rates. Regular report presentations provide complete progress transparency, promoting data-driven decisions.',
        ],
      },
    ],
  },
  howToManageTeamSection: {
    title: 'Seamless dedicated team management',
    description: [
      'A DT is a powerful resource, but its success relies on strategic management. Agilie removes the managerial burden from your shoulders by using a proactive approach.',
    ],
    imageSrc: '/services-images/dedicated-teams/manage.png',
    advices: [
      {
        title: 'Daily/weekly operational support',
        description: [
          'Our project managers monitor team coordination within daily stand-ups and weekly retrospectives to measure progress and ensure adherence to the agreed-upon processes, while you receive cohesive and unhindered project development.',
        ],
      },
      {
        title: 'Proactive error resolution',
        description: [
          'We enforce stringent code review and QA standards. If the error occurs, we implement the root cause analysis, with resource distribution to fix bugs. Then, QA specialists provide clients with transparent reporting on the problem-solving path.',
        ],
      },
      {
        title: 'Talent retention & stability',
        description: [
          'Agilie covers all HR functions, including payroll, benefits, performance reviews, and career planning for employees. We address the retention risks by offering our workers growth opportunities and preserving institutional knowledge.',
        ],
      },
      {
        title: 'Continuous process optimization',
        description: [
          'Our company streamlines agile workflows by continuously monitoring the team’s methodology and tech stack for areas of improvement. This promotes quality enhancement and improves workload optimization.',
        ],
      },
      {
        title: 'Flexible scaling',
        description: [
          'When you need to scale for the feature launch or scale down during the maintenance period, Agilie vendors quickly integrate new specialists to manage the transition of the existing members, ensuring a seamless skill augmentation.',
        ],
      },
    ],
  },
  bestOptionSection: {
    title: 'Why Agilie is the best option?',
    description:
      'Agilie is your high-performing go-to partner in diverse industry sectors that seek software solutions.',
    industriesTitle: 'Industries where we can help',
    industries: [
      {
        iconSrc: '/services-images/dedicated-teams/fintech-icon.png',
        title: 'Fintech',
        text: 'High-powered financial tools that incorporate Big Data and ML capabilities for advanced risk control.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/real-estate-icon.png',
        title: 'Real estate',
        text: 'Real estate software with secure payments and automated escrow, minimizing manual banking tasks.',
      },
      {
        iconSrc: '/services-images/area-competance/media.png',
        title: 'Media',
        text: 'Platforms built for media campaigns to present the product and achieve worldwide visibility.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/healthcare.png',
        title: 'Healthcare',
        text: 'HIPAA-compliant platforms for telemedicine, billing, and secure patient data management.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/marketplace-icon.png',
        title: 'Marketplace & eCom',
        text: 'Building trusted marketplaces with highly secure transaction spaces to adhere to industry-specific standards.',
      },

      {
        iconSrc: '/services-images/dedicated-teams/transportation-&-logistics-icon.png',
        title: 'Logistics',
        text: 'Logistic software for complete control over inventory and warehousing to optimize supply chains.',
      },

      {
        iconSrc: '/services-images/dedicated-teams/healthcare.png',
        title: 'Pharmacy',
        text: 'Cloud pharma solutions to automate inventory, stock replenishment, and prescription flows.',
      },
      {
        iconSrc: '/services-images/dedicated-teams/banking-icon.png',
        title: 'Banking',
        text: 'Mobile banking apps for account opening, loans, 24/7 management, P2P transfers, and wallets.',
      },
    ],
    card: {
      imageSrc: '/services-images/dedicated-teams/best-option.png',
      title: 'Your benefits with Agilie as a dedicated team partner',
      text: [],
      list: [
        'Access a global talent pool of different seniority levels to secure dedicated and reliable full-time engineers.',
        'Reduce the time and financial input spent on recruitment and associated overhead expenditures.',
        'Leverage maximum flexibility to quickly scale the team size and composition as needed.',
        'Achieve faster delivery by integrating a high-performing team that uses a well-established development process.',
        'Dictate the project priorities, choose management tools, and define the technical workflows to ensure the team’s full alignment.',
      ],
    },
  },
  outsourcingToDedicatedTeamsSection: {
    imageSrc: `/services-images/dedicated-teams/outsource.png`,
    title: 'Hire a dedicated development team',
    text: [
      'The dedicated team of developers helps businesses cut recruitment expenses and reduce office overhead, as experts work with their own tools and resources. The vendor takes full responsibility for assembling the right talents, ensuring the tasks are completed with minimal errors. If the developer’s skills prove insufficient during the initial stage, the vendor replaces them with no additional cost or delays.',
      'To guarantee quality, a Service Level Agreement (SLA) is applied, obliging the vendor to maintain clean code, resolve bugs efficiently, and keep project management and technical support at scale. In addition, the vendor handles team scaling and retention, quickly filling vacancies to keep the project pipeline operating smoothly and ensure the delivery timelines are unaffected.',
    ],
    list: [],
  },
  newProjectSection: {
    title: `Ready to start your project?`,
    buttonText: `Start a project`,
  },
  FAQSection: {
    title: `FAQ`,
    accordionItems: [
      {
        question: 'Why hire a dedicated development team instead of freelancers or in-house developers?',
        answers: [
          'A dedicated team offers greater stability, cost predictability, profound expertise, and centralized management over the development progress, as compared to freelancers. ',
        ],
      },
      {
        question: 'How much control will I have over my dedicated team?',
        answers: [
          'You will have full control over the project scope, priorities, and workflow, while the team will be flexible to any change requests.',
        ],
      },
      {
        question: 'What is the typical structure of a dedicated development team?',
        answers: [
          'The typical structure of the dedicated team consists of the PM, business analyst, UX/UI designer, developers, and QA engineers. ',
        ],
      },
      {
        question: 'How long does it take to set up a dedicated development team?',
        answers: [
          'It depends on the overall required size and niche specificity. Usually, the assembling team takes 4 to 6 weeks from initial agreement to the full integration into your project.',
        ],
      },
      {
        question: 'Can I integrate a dedicated team with my in-house developers?',
        answers: [
          'Dedicated teams can be integrated with your existing staff, sharing tools, processes, and knowledge to function as a cohesive unit.',
        ],
      },
    ],
  },
  startBusiness: {
    title: 'Build your dedicated team cooperation with Agilie',
    description:
      "Ready to scale with a dedicated team? Fill out the form. We'll contact you to clarify details and help your business grow.",
  },
};
